import React, { useEffect, useState } from "react";
import supabase from "../../supabase";
import { PlusCircle, Pencil, Trash } from 'lucide-react';
import Loader from "../../Common/Loader";
import AddExpensePopup from "../PopUpComponents/AddExpensePopup";
import { useToast } from '../Invoice/Toast';
import "./Expenses.css";

export default function Expenses({ modalState, openModal, closeModal }) {
  const [expenses, setExpenses] = useState([]);
  const [form, setForm] = useState({ 
    category: "", 
    amount: "", 
    description: "", 
    expense_date: "" 
  });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Delete Popup States
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  const { showError, showSuccess, ToastContainer } = useToast();

  // Helper function to create URL-friendly slug
  const createSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };

  // Handle URL-based modal state changes
  useEffect(() => {
    if (!modalState || expenses.length === 0) return;

    const { action, id } = modalState;

    if (action === 'add') {
      resetForm();
    } else if (action === 'edit' && id) {
      loadExpenseForEdit(id);
    } else if (!action) {
      resetForm();
    }
  }, [modalState, expenses]);

  // Load expense for editing by ID or slug
  const loadExpenseForEdit = (expenseIdOrSlug) => {
    // First try to find by ID
    let expense = expenses.find(e => e.id === expenseIdOrSlug);
    
    // If not found, try matching by category slug
    if (!expense) {
      expense = expenses.find(e => {
        const expectedSlug = createSlug(e.category);
        return expectedSlug === expenseIdOrSlug;
      });
    }

    if (expense) {
      setForm({
        category: expense.category,
        amount: expense.amount,
        description: expense.description,
        expense_date: expense.expense_date,
      });
      setEditingId(expense.id);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("expenses")
        .select("*")
        .order("expense_date", { ascending: false });
      
      if (error) {
        console.error(error);
        showError('Load Error', 'Failed to load expense entries');
      } else {
        setExpenses(data || []);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred while loading expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const resetForm = () => {
    setForm({ category: "", amount: "", description: "", expense_date: "" });
    setEditingId(null);
  };

  const handleAddExpenseSave = async () => {
    if (!form.category.trim()) {
      showError('Validation Error', 'Category is required');
      return;
    }

    if (!form.amount || parseFloat(form.amount) <= 0) {
      showError('Validation Error', 'Please enter a valid amount');
      return;
    }

    setUploading(true);
    
    try {
      const user = await supabase.auth.getUser();
      
      if (!user.data.user) {
        showError('Authentication Error', 'User not authenticated');
        setUploading(false);
        return;
      }

      const payload = {
        ...form,
        user_id: user.data.user.id,
        amount: parseFloat(form.amount),
        expense_date: form.expense_date || new Date().toISOString().slice(0, 10),
      };

      let error;

      if (editingId) {
        const { error: updateError } = await supabase
          .from("expenses")
          .update(payload)
          .eq("id", editingId);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from("expenses")
          .insert([payload]);
        error = insertError;
      }

      if (error) {
        console.error(error);
        showError('Save Error', error.message || (editingId ? 'Failed to update expense' : 'Failed to add expense'));
      } else {
        showSuccess('Success', editingId ? 'Expense updated successfully!' : 'Expense added successfully!');
        resetForm();
        if (closeModal) closeModal();
        fetchExpenses();
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred');
    } finally {
      setUploading(false);
    }
  };

  const handleAddExpenseCancel = () => {
    resetForm();
    if (closeModal) closeModal();
  };

  const handleEdit = (expense, e) => {
    e.stopPropagation();
    if (openModal) {
      openModal('edit', {
        id: expense.id,
        name: expense.category
      });
    }
  };

  const openDeletePopup = (expense, e) => {
    e.stopPropagation();
    setExpenseToDelete(expense);
    setShowDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setExpenseToDelete(null);
  };

  const deleteExpense = async () => {
    if (!expenseToDelete) return;

    const expense = expenseToDelete;
    closeDeletePopup();

    try {
      const { error } = await supabase
        .from("expenses")
        .delete()
        .eq("id", expense.id);

      if (error) {
        console.error(error);
        showError('Delete Error', error.message || 'Failed to delete expense entry');
      } else {
        showSuccess('Success', `Expense "${expense.category}" deleted successfully`);
        fetchExpenses();
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred');
    }
  };

  const handleAddExpenseClick = () => {
    resetForm();
    if (openModal) {
      openModal('add');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Determine if popup should show based on modalState
  const showAddExpensePopup = modalState?.action === 'add' || modalState?.action === 'edit';

  if (loading) {
    return (
      <div className="Expensesinfo-loading">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <ToastContainer />

      <div className="Expensesinfo-container">
        <div className="Expensesinfo-header">
          <div className="Expensesinfo-header-left">
            <h1 className="Expensesinfo-title">Expense Manager</h1>
          </div>
          
          <div className="Expensesinfo-header-right">
            <button 
              className="Expensesinfo-add-btn"
              onClick={handleAddExpenseClick}
            >
              Add Expense
              <PlusCircle size={18} className="add-icon" />
            </button>
          </div>
        </div>

        {expenses.length === 0 ? (
          <div className="Expensesinfo-empty">
            <div className="Expensesinfo-empty-icon">💰</div>
            <h3 className="Expensesinfo-empty-title">No expenses yet</h3>
            <p className="Expensesinfo-empty-description">
              Add your first expense above to start tracking your spending.
            </p>
            <button 
              className="Expensesinfo-create-first-expense-btn"
              onClick={handleAddExpenseClick}
            >
              Add Your First Expense
            </button>
          </div>
        ) : (
          <div className="Expensesinfo-content">
            <div className="Expensesinfo-table-view">
              <div className="Expensesinfo-table-wrapper">
                <div className="Expensesinfo-table-header-container">
                  <table className="Expensesinfo-table-header">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                
                <div className="Expensesinfo-table-body-container">
                  <table className="Expensesinfo-table">
                    <tbody>
                      {expenses.map((exp) => (
                        <tr className="Expensesinfo-table-row" key={exp.id}>
                          <td className="Expensesinfo-table-cell Expensesinfo-date-cell">
                            {formatDate(exp.expense_date)}
                          </td>
                          <td className="Expensesinfo-table-cell category-cell">{exp.category}</td>
                          <td className="Expensesinfo-table-cell description-cell">{exp.description || 'N/A'}</td>
                          <td className="Expensesinfo-table-cell amount-cell">₹{parseFloat(exp.amount).toFixed(2)}</td>
                          <td className="Expensesinfo-table-cell actions-cell">
                            <button 
                              className="Expensesinfo-action-btn edit-btn" 
                              onClick={(e) => handleEdit(exp, e)}
                              title="Edit Expense"
                            >
                              <Pencil size={18} />
                            </button>
                            <button 
                              className="Expensesinfo-action-btn exp-delete-btn" 
                              onClick={(e) => openDeletePopup(exp, e)}
                              title="Delete Expense"
                            >
                              <Trash size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="Expensesinfo-card-view">
              {expenses.map((exp) => (
                <div key={exp.id} className="Expensesinfo-card">
                  <div className="Expensesinfo-card-header">
                    <div className="Expensesinfo-card-title">
                      <h4>{exp.category}</h4>
                      <span className="Expensesinfo-card-amount">
                        ₹{parseFloat(exp.amount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="Expensesinfo-card-body">
                    <div className="Expensesinfo-card-field">
                      <span className="Expensesinfo-card-label">Date:</span>
                      <span className="Expensesinfo-card-value">{formatDate(exp.expense_date)}</span>
                    </div>
                    <div className="Expensesinfo-card-field">
                      <span className="Expensesinfo-card-label">Description:</span>
                      <span className="Expensesinfo-card-value">{exp.description || 'N/A'}</span>
                    </div>
                  </div>
                  
                  <div className="Expensesinfo-card-actions">
                    <button
                      className="Expensesinfo-action-btn edit-btn"
                      onClick={(e) => handleEdit(exp, e)}
                      title="Edit Expense"
                    >
                      <Pencil size={16} />
                      <span className="Expensesinfo-btn-text">Edit</span>
                    </button>
                    
                    <button
                      className="Expensesinfo-action-btn exp-delete-btn"
                      onClick={(e) => openDeletePopup(exp, e)}
                      title="Delete Expense"
                    >
                      <Trash size={16} />
                      <span className="Expensesinfo-btn-text">Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showAddExpensePopup && (
          <AddExpensePopup
            form={form}
            setForm={setForm}
            onSave={handleAddExpenseSave}
            onCancel={handleAddExpenseCancel}
            isEditing={!!editingId}
            title={editingId ? 'Edit Expense' : 'Add New Expense'}
            saveButtonText={editingId ? 'Update Expense' : 'Add Expense'}
            uploading={uploading}
            onRefresh={fetchExpenses}
          />
        )}

        {showDeletePopup && expenseToDelete && (
          <div className="delete-popup-overlay" onClick={closeDeletePopup}>
            <div className="delete-popup" onClick={e => e.stopPropagation()}>
              <div className="delete-popup-header">
                <div className="delete-popup-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h3 className="delete-popup-title">Delete Expense Entry</h3>
              </div>
              
              <div className="delete-popup-content">
                <p className="delete-popup-message">
                  Are you sure you want to delete expense <strong>{expenseToDelete.category}</strong>?
                </p>
                
                <div className="delete-popup-info">
                  <div className="delete-popup-info-item">
                    <span className="delete-popup-label">Amount:</span>
                    <span className="delete-popup-value">₹{parseFloat(expenseToDelete.amount).toFixed(2)}</span>
                  </div>
                  {expenseToDelete.description && (
                    <div className="delete-popup-info-item">
                      <span className="delete-popup-label">Description:</span>
                      <span className="delete-popup-value">{expenseToDelete.description}</span>
                    </div>
                  )}
                  <div className="delete-popup-info-item">
                    <span className="delete-popup-label">Date:</span>
                    <span className="delete-popup-value">{formatDate(expenseToDelete.expense_date)}</span>
                  </div>
                </div>

                <div className="delete-popup-warning">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <span>This action cannot be undone.</span>
                </div>
              </div>
              
              <div className="delete-popup-actions">
                <button className="delete-popup-btn delete-popup-btn-cancel" onClick={closeDeletePopup}>
                  Cancel
                </button>
                <button className="delete-popup-btn delete-popup-btn-delete" onClick={deleteExpense}>
                  Delete Expense
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}